import React, { useCallback, useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router";
import apiClient from "../../../utils/apiClient";
import * as Sentry from "@sentry/react";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";
import WhatsAppIcon from "../../contact/img/whatsapp.svg";

export const CHECKOUT_TYPE_STORAGE_KEY = "cyt_checkout_type";

const PaymentReturn = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("loading"); // loading, success, error
  const [sessionInfo, setSessionInfo] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [checkoutType, setCheckoutType] = useState(() => {
    if (typeof window === "undefined") return "property";
    return (
      searchParams.get("checkout_type") ||
      window.sessionStorage.getItem(CHECKOUT_TYPE_STORAGE_KEY) ||
      "property"
    );
  });

  const checkSessionStatus = useCallback(
    async (sessionId) => {
      try {
        await new Promise((resolve) => setTimeout(resolve, 2000));

        const { data } = await apiClient.get(`/transactions/session-status/${sessionId}`);

        if (data.status === "paid" || data.status === "complete") {
          const completedCheckoutType =
            searchParams.get("checkout_type") ||
            window.sessionStorage.getItem(CHECKOUT_TYPE_STORAGE_KEY) ||
            "property";
          const destination =
            completedCheckoutType === "subscription" ? "/subscription" : "/properties";

          setCheckoutType(completedCheckoutType);
          window.sessionStorage.removeItem(CHECKOUT_TYPE_STORAGE_KEY);
          setStatus("success");
          setSessionInfo(data);

          setTimeout(() => {
            navigate(destination);
          }, 3000);
        } else if (data.status === "pending") {
          setStatus("processing");
          setTimeout(() => checkSessionStatus(sessionId), 2000);
        } else {
          setStatus("error");
          setErrorMessage("El pago no se completó correctamente.");
        }
      } catch (err) {
        console.error("Error verificando estado del pago:", err);
        Sentry.captureException(err);

        setStatus("error");
      }
    },
    [navigate, searchParams],
  );

  useEffect(() => {
    const sessionId = searchParams.get("session_id");

    if (!sessionId) {
      setStatus("error");
      setErrorMessage("No se encontró información de la sesión de pago.");
      return;
    }

    checkSessionStatus(sessionId);
  }, [checkSessionStatus, searchParams]);

  const renderContent = () => {
    const isSubscription = checkoutType === "subscription";
    const destination = isSubscription ? "/subscription" : "/properties";

    switch (status) {
      case "loading":
      case "processing":
        return (
          <div className="text-center">
            <Loader2 className="w-16 h-16 text-blue-500 animate-spin mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Procesando tu pago...</h2>
            <p className="text-gray-600">Por favor espera mientras confirmamos tu pago.</p>
          </div>
        );

      case "success":
        return (
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {isSubscription ? "¡Suscripción activada!" : "¡Pago exitoso!"}
            </h2>
            <p className="text-gray-600 mb-4">
              {isSubscription
                ? "Tu suscripción se activó correctamente."
                : "Tu propiedad ha sido publicada correctamente."}
            </p>
            <p className="text-sm text-gray-500">
              Serás redirigido automáticamente en unos segundos...
            </p>
            <button
              onClick={() => navigate(destination)}
              className="mt-6 px-6 py-3 bg-black text-white rounded-md hover:bg-gray-800 transition-colors"
            >
              {isSubscription ? "Ver mi suscripción" : "Ver mis propiedades"}
            </button>
          </div>
        );

      case "error":
        return (
          <div className="text-center">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Hubo un problema</h2>
            <p className="text-gray-600 mb-4">{errorMessage || "No pudimos procesar tu pago."}</p>
            <div className="flex gap-4 justify-center">
              <button
                onClick={() =>
                  window.open(
                    `https://api.whatsapp.com/send?phone=+523317150016&text=Hola, hubo un error al procesar mi pago. session_id: ${
                      sessionInfo?.id || "N/A"
                    }`,
                    "_blank",
                  )
                }
                className="mt-4 flex h-10 w-full items-center justify-items-center rounded bg-green-whatsapp text-white hover:bg-green-whatsapp-darker"
              >
                <div className="m-auto flex md:px-2">
                  <img
                    alt="ícono whatsapp"
                    className="mx-2 w-4"
                    referrerPolicy="no-referrer-when-downgrade"
                    src={WhatsAppIcon}
                  />
                  <span className="mx-2">Contactar soporte</span>
                </div>
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">{renderContent()}</div>
    </div>
  );
};

export default PaymentReturn;
